import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Maximize2, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function WarehouseKpiWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKpi();
  }, []);

  const fetchKpi = async () => {
    try {
      const data = await apiRequest('/warehouse/kpi/manager');
      setKpi(data);
    } catch (error) {
      console.error('Failed to fetch warehouse KPI');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (level) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-amber-600 bg-amber-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  return (
    <Card data-testid="warehouse-kpi-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-lg">Warehouse KPIs</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/warehouse')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : !kpi ? (
          <div className="text-center py-4 text-slate-400">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No KPI data</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <span className="text-sm text-slate-600">Dispatch Accuracy</span>
              <div className="flex items-center gap-2">
                <span className="font-bold">{kpi.dispatch_accuracy?.toFixed(1)}%</span>
                <Badge className={getScoreColor(kpi.dispatch_score?.level)}>
                  {kpi.dispatch_score?.level?.toUpperCase()}
                </Badge>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
              <span className="text-sm text-slate-600">Delivery Success</span>
              <div className="flex items-center gap-2">
                <span className="font-bold">{kpi.ok_percentage?.toFixed(1)}%</span>
                <Badge className={getScoreColor(kpi.reporting_score?.level)}>
                  {kpi.reporting_score?.level?.toUpperCase()}
                </Badge>
              </div>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Week {kpi.week_number} • {kpi.total_deliveries || 0} deliveries
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
